import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { useProjectPhotos } from "@/hooks/useProjectPhotos";
import { Loader2 } from "lucide-react";

interface ProjectCarouselProps {
  filter?: "all" | "metal" | "tile" | "shingle" | "flat";
  state?: string;
  className?: string;
}

const ProjectCarousel = ({
  filter = "all",
  state,
  className,
}: ProjectCarouselProps) => {
  const { data: photos, isLoading } = useProjectPhotos(filter, state);

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  if (!photos || photos.length === 0) {
    return (
      <div className={cn("text-center py-8", className)}>
        <p className="text-muted-foreground text-sm">
          No project photos available yet.
        </p>
      </div>
    );
  }

  return (
    <Carousel
      opts={{
        align: "start",
        loop: true,
      }}
      className={cn("w-full", className)}
    >
      <CarouselContent className="-ml-2 md:-ml-4">
        {photos.map((image) => (
          <CarouselItem
            key={image.id}
            className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2 md:basis-1/3"
          >
            <div className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-card">
              <img
                src={image.image_url}
                alt={image.description || `${image.roof_type} roof project`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                  {image.roof_type} Roof
                </span>
                {image.location && (
                  <span className="text-white font-medium">{image.location}</span>
                )}
              </div>
              {/* Type Badge */}
              <div className="absolute top-3 right-3 bg-accent/90 text-white text-xs font-semibold px-2 py-1 rounded-full capitalize">
                {image.roof_type}
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="hidden md:flex -left-4" />
      <CarouselNext className="hidden md:flex -right-4" />
    </Carousel>
  );
};

export default ProjectCarousel;
